"use client";
import React, {JSX, useCallback, useEffect, useState} from "react";

import {useSnapshot} from "valtio";

import {CouponView} from "@/features/coupon/coupon.view";
import {CheckAvailabilityInstance} from "@/features/rental/check-availability/check-availability.model";
import {DateErrorAlert} from "@/features/rental/check-availability/parts/date-error-alert";
import {GuestErrorAlert} from "@/features/rental/check-availability/parts/guest-error-alert";
import {PricingSkeleton} from "@/features/rental/check-availability/parts/pricing-skeleton";
import {GuestsView} from "@/features/rental/guests";

import {useMatchMedia} from "@/shared/hooks";
import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons";
import {DateRangePickerField} from "@/shared/ui/form/datepickers";
import {DateRangeAvailabilityHeader, DateRangeFilterTrigger} from "@/shared/ui/form/datepickers/range/parts";
import {isCouponEnabled} from "@/shared/utils/env";

interface Props {
    className?: string;
    hiddenAction?: boolean;
    hideHeaderInputs?: boolean;
    /** Rendered to the right of the calendar (e.g. mobile next-month arrow). Passed to DateRangePickerField in inline mode. */
    rightOfCalendar?: React.ReactNode;
    model: CheckAvailabilityInstance;
}

export function BlockActions({className, model, hiddenAction, hideHeaderInputs = false, rightOfCalendar}: Props): JSX.Element {
    const {t} = useTranslation("booking");
    const {t: tRental} = useTranslation("rental");
    const snapshot = useSnapshot(model);
    const formFeedback = useSnapshot(model.inquiryForm.submitFeedback);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const isMobile = useMatchMedia({maxWidth: 767});
    // Wrapper to translate tooltip messages
    // Model returns {key, params?} object, we convert it to string for DateRangePickerField
    const getDayTooltip = (date: Date): string | null => {
        const tooltip = model.getDayTooltip(date);
        if (!tooltip) return null;

        // Translate using key and optional params
        return tRental(tooltip.key as Parameters<typeof tRental>[0], tooltip.params);
    };

    const handleButtonClick = useCallback((): void => {
        if (snapshot.hasDates) {
            void model.submit();
        } else {
            setIsCalendarOpen(true);
        }
    }, [snapshot, setIsCalendarOpen]);

    const handleCalendarOpenChange = useCallback(
        (open: boolean): void => {
            // Prevent calendar from closing when loading
            if (!open && snapshot.isItineraryLoading) {
                return;
            }
            setIsCalendarOpen(open);
        },
        [setIsCalendarOpen, snapshot],
    );

    useEffect(() => {
        return model.events.on("noCheckoutDatesInView", (payload: {months: Array<{month: number; year: number}>}) => {
            const dr = model.dateRange;
            const matchesSecond = payload.months.some((m) => m.month === dr.secondViewMonth && m.year === dr.secondViewYear);
            if (matchesSecond) {
                model.dateRange.nextMonth();
            }
        });
    }, [model]);

    // showCoupon is enabled only for streamline units and coupons are not available for inquiry units.
    const unitQualifiesForCoupon = snapshot.showCoupon && snapshot.unitBookingType === "checkout";

    return (
        <div className={className}>
            <DateRangePickerField
                inlineOnMobile={true}
                showClearButton={false}
                dropdownZIndex={20}
                renderTrigger={(props) => <DateRangeFilterTrigger {...props} onClick={() => setIsCalendarOpen(true)} />}
                renderHeader={(headerProps) => (
                    <DateRangeAvailabilityHeader
                        {...headerProps}
                        onClear={model.dateRange.clear}
                        showNightsSummary
                        showDateInputs={!hideHeaderInputs}
                    />
                )}
                model={model.dateRange}
                label={t("dates")}
                startPlaceholder={t("checkIn")}
                endPlaceholder={t("checkOut")}
                controlledIsOpen={isCalendarOpen}
                onOpenChange={handleCalendarOpenChange}
                getDayTooltip={getDayTooltip}
                getDayClassName={model.getDayClassName}
                hideOtherMonthDaysContent={isMobile}
                rightOfCalendar={rightOfCalendar}
                isLoading={snapshot.isItineraryLoading || snapshot.calendarQuery.isPending || snapshot.calendarQuery.isIdle}
            />

            <GuestsView
                showPetsSubtitle={true}
                usePetsToggle={false}
                model={model.guests}
                label={t("guests")}
                className="check-availability__guests"
            />

            <GuestErrorAlert errors={[...snapshot.guestErrors]} />

            {unitQualifiesForCoupon && isCouponEnabled() && <CouponView model={model.coupon} className="check-availability__coupon" />}

            {!hiddenAction && (
                <div className="check-availability__action">
                    <ButtonBase
                        variant="primary"
                        size="lg-fixed"
                        width="full"
                        onClick={handleButtonClick}
                        className="check-availability__submit"
                        loading={snapshot.isActionLoading}
                    >
                        {snapshot.hasDates ? (snapshot.bookingType === "inquiry" ? t("propertyInquiry") : t("reserve")) : t("checkAvailability")}
                    </ButtonBase>
                    {formFeedback.isSuccess ? (
                        <p className="check-availability__inquiry-sent">{t("inquirySent")}</p>
                    ) : (
                        <p className="check-availability__disclaimer">{t("youWontBeCharged")}</p>
                    )}
                </div>
            )}

            <DateErrorAlert
                minNightsError={snapshot.minNightsError}
                minNightsErrorMessage={snapshot.minNightsErrorMessage}
                dateUnavailableError={snapshot.dateUnavailableError}
            />

            <div className="check-availability__pricing">
                <PricingSkeleton
                    loading={snapshot.isQuoteLoading}
                    rentSummary={snapshot.rentSummary}
                    feesAmounts={[...snapshot.feesAmounts]}
                    couponDiscount={snapshot.couponDiscount}
                    totalPrice={snapshot.totalPrice}
                />
            </div>
        </div>
    );
}
